// Página UserManagement.js com novo design Tailwind

import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../store/authContext';
import AccessDenied from '../components/UI/AccessDenied';

// Importe ícones do Lucide
import { 
  Users, 
  UserPlus, 
  User, 
  Shield, 
  Mail, 
  Key, 
  Edit, 
  AlertTriangle, 
  CheckCircle,
  Search,
  Filter,
  Lock,
  X,
  UserCheck,
  Clock,
  Download
} from 'lucide-react';

const UserManagement = () => {
  const { currentUser, addUser, updateUser, hasPermission } = useContext(AuthContext);

  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({ username: '', password: '', name: '', email: '', role: 'basic' });
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');

  const isAdmin = hasPermission('view_team') && hasPermission('edit_team');

  useEffect(() => {
    if (isAdmin) {
      const usersData = JSON.parse(localStorage.getItem('usersData'));
      if (usersData?.users) setUsers(usersData.users);
    }
  }, [isAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.username || (!editingUser && !formData.password) || !formData.name || !formData.email) {
      return setError('Por favor, preencha todos os campos obrigatórios.');
    }

    if (!editingUser && formData.password.length < 6) {
      return setError('A senha deve ter pelo menos 6 caracteres.');
    }

    const result = editingUser
      ? updateUser(editingUser.id, formData)
      : addUser(formData);

    if (result.success) {
      setSuccess(editingUser ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
      const usersData = JSON.parse(localStorage.getItem('usersData'));
      if (usersData?.users) setUsers(usersData.users);
      setFormData({ username: '', password: '', name: '', email: '', role: 'basic' });
      setEditingUser(null);
    } else {
      setError(result.message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ username: user.username, password: '', name: user.name, email: user.email, role: user.role });
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setFormData({ username: '', password: '', name: '', email: '', role: 'basic' });
    setError('');
    setSuccess('');
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const getRoleLabel = (role) => {
    const roles = { 
      admin: 'Administrador', 
      manager: 'Gerente', 
      marketing: 'Marketing', 
      basic: 'Básico' 
    };
    return roles[role] || role;
  };

  const getRoleDescription = (role) => {
    const descriptions = {
      admin: 'Acesso total ao sistema, incluindo gerenciamento de usuários e configurações.',
      manager: 'Acesso a dashboard, tráfego, produtos e colaboradores. Sem acesso a configurações avançadas.',
      marketing: 'Acesso a campanhas de marketing, produtos e relatórios de desempenho.',
      basic: 'Acesso básico a produtos e tráfego, apenas visualização limitada.'
    };
    return descriptions[role] || '';
  };

  const getRoleIcon = (role) => {
    const icons = {
      admin: <Shield className="h-5 w-5 text-purple-600" />,
      manager: <UserCheck className="h-5 w-5 text-blue-600" />,
      marketing: <User className="h-5 w-5 text-green-600" />,
      basic: <User className="h-5 w-5 text-gray-600" />
    };
    return icons[role] || <User className="h-5 w-5 text-gray-600" />;
  };

  // Filtragem de usuários
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === '' || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  if (!isAdmin) return <AccessDenied message="Apenas administradores podem acessar esta página." />;

  return (
    <div className="page-container">
      <div className="page-header flex justify-between items-center mb-6">
        <h1 className="page-title">Gerenciamento de Usuários</h1>
        
        <div className="hidden md:flex items-center space-x-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar usuários..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={handleSearch}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <select 
            className="form-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">Todos os perfis</option>
            <option value="admin">Administrador</option>
            <option value="manager">Gerente</option>
            <option value="marketing">Marketing</option>
            <option value="basic">Básico</option>
          </select>
        </div>
      </div>

      {/* Busca em dispositivos móveis */}
      <div className="md:hidden mb-6 flex flex-col space-y-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar usuários..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={searchTerm}
            onChange={handleSearch}
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
        <select 
          className="form-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">Todos os perfis</option>
          <option value="admin">Administrador</option>
          <option value="manager">Gerente</option>
          <option value="marketing">Marketing</option>
          <option value="basic">Básico</option>
        </select>
      </div>

      <div className="card mb-6">
        <div className="card-header">
          <h2 className="card-title m-0 flex items-center">
            {editingUser ? (
              <>
                <Edit className="h-5 w-5 mr-2 text-blue-600" />
                Editar Usuário: {editingUser.name}
              </>
            ) : (
              <>
                <UserPlus className="h-5 w-5 mr-2 text-blue-600" />
                Novo Usuário
              </>
            )}
          </h2>
          {editingUser && (
            <button 
              onClick={handleCancelEdit}
              className="btn-outline text-sm"
            >
              Cancelar Edição
            </button>
          )}
        </div>
        
        <div className="p-6">
          {error && (
            <div className="alert alert-danger mb-6">
              <div className="alert-icon">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h3 className="font-medium">Erro</h3>
                <p>{error}</p>
              </div>
              <button onClick={() => setError('')} className="ml-auto text-red-700 hover:text-red-900" aria-label="Fechar">
                <X size={16} />
              </button>
            </div>
          )}
          
          {success && (
            <div className="alert alert-success mb-6">
              <div className="alert-icon">
                <CheckCircle size={20} />
              </div>
              <div>
                <h3 className="font-medium">Sucesso</h3>
                <p>{success}</p>
              </div>
              <button onClick={() => setSuccess('')} className="ml-auto text-green-700 hover:text-green-900" aria-label="Fechar">
                <X size={16} />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label">Usuário</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type="text" 
                    name="username" 
                    placeholder="Nome de usuário" 
                    className="form-input pl-10" 
                    value={formData.username} 
                    onChange={handleChange} 
                    disabled={editingUser} 
                    required 
                  />
                </div>
                {editingUser && (
                  <p className="text-xs text-gray-500 mt-1">O nome de usuário não pode ser alterado</p>
                )}
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  {editingUser ? 'Nova Senha (opcional)' : 'Senha'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    name="password" 
                    placeholder={editingUser ? "Nova senha (deixe em branco para manter)" : "Senha (mínimo 6 caracteres)"} 
                    className="form-input pl-10 pr-10" 
                    value={formData.password} 
                    onChange={handleChange} 
                    required={!editingUser} 
                  />
                  <button 
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 
                      <Eye className="h-5 w-5 text-gray-400" /> : 
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    }
                  </button>
                </div>
                {!editingUser && (
                  <p className="text-xs text-gray-500 mt-1">Mínimo de 6 caracteres</p>
                )}
              </div>
              
              <div className="form-group">
                <label className="form-label">Nome Completo</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type="text" 
                    name="name" 
                    placeholder="Nome completo" 
                    className="form-input pl-10" 
                    value={formData.name} 
                    onChange={handleChange} 
                    required 
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="Email" 
                    className="form-input pl-10" 
                    value={formData.email} 
                    onChange={handleChange} 
                    required 
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Perfil de Acesso</label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
                {['admin', 'manager', 'marketing', 'basic'].map(role => (
                  <div 
                    key={role}
                    className={`
                      border rounded-lg p-4 cursor-pointer transition-colors
                      ${formData.role === role 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                    onClick={() => setFormData(prev => ({ ...prev, role }))}
                  >
                    <div className="flex items-center mb-2">
                      {getRoleIcon(role)}
                      <span className="ml-2 font-medium">{getRoleLabel(role)}</span>
                    </div>
                    <p className="text-sm text-gray-600">{getRoleDescription(role)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              {editingUser && (
                <button 
                  type="button" 
                  onClick={handleCancelEdit} 
                  className="btn-outline"
                >
                  Cancelar
                </button>
              )}
              <button type="submit" className="btn-primary">
                {editingUser ? 'Salvar Alterações' : 'Adicionar Usuário'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title m-0 flex items-center">
            <Users className="h-5 w-5 mr-2 text-blue-600" />
            Lista de Usuários
          </h2>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-600">
              {filteredUsers.length} usuário{filteredUsers.length !== 1 ? 's' : ''}
            </span>
            <button className="btn-outline text-sm flex items-center">
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </button>
          </div>
        </div>
        
        <div className="table-container">
          {filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">
                {searchTerm || roleFilter
                  ? 'Nenhum usuário encontrado para sua busca.'
                  : 'Nenhum usuário cadastrado ainda.'}
              </p>
              <button 
                className="btn-primary flex items-center mx-auto"
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                }}
              >
                {searchTerm || roleFilter ? (
                  <>
                    <Filter className="h-4 w-4 mr-2" />
                    Limpar Filtros
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Adicionar Usuário
                  </>
                )}
              </button>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Perfil
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criado em
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          {user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">@{user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getRoleIcon(user.role)}
                        <span className="ml-2 text-sm font-medium">{getRoleLabel(user.role)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(user)}
                        disabled={user.id === currentUser?.id}
                        className={`flex items-center text-blue-600 hover:text-blue-800 ${user.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Editar
                      </button>
                      {user.id === currentUser?.id && (
                        <p className="text-xs text-gray-500 mt-1">Usuário atual</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {filteredUsers.length > 0 && (
          <div className="card-footer flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Mostrando {filteredUsers.length} de {users.length} usuários
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                Anterior
              </button>
              <button className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Adicione os componentes Eye e EyeOff do Lucide
const Eye = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOff = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" x2="22" y1="2" y2="22" />
  </svg>
);

export default UserManagement;
